import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Spinner, Alert } from "reactstrap";
import { Button, Icon } from "../../Component";
import { Input, Modal, Pagination, Table, Tabs, Tag } from "antd";
import * as XLSX from "xlsx";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ReactTyped } from "react-typed";
import FormInput from "../../../pages/form-components/InputComponent";
import { uploadApis } from "../../../api/Uploads";
import { UploadReportState } from "./uploadReport";
import { useUploadReportPoll } from "./useUploadReportPoll";
import { downloadTemplateFromBlob } from "./templateDownload";
import {
  addPendingJob,
  removePendingJob,
} from "../../../store/slices/misc/pendingJobsSlice";
import { setUploadHighlight } from "../../../store/slices/misc/highlightSlice";

// CRM-style stage machine (see prx-crm-ui inventory bulk upload):
// upload → validating → preview → committing → done | failed
// The browser only kicks jobs off and polls — validation and creation run on
// backend workers, so closing the modal mid-flight loses nothing.
// Extracted from the norms upload modal; per-type behavior comes in through
// ExcelUploadTypeConfig, everything else is deliberately non-configurable.
type UploadStage =
  | "upload"
  | "validating"
  | "preview"
  | "committing"
  | "done"
  | "failed";

export interface UploadRowBucket {
  key: string;
  label: string;
  rows: any[];
}

export type UploadGateResult =
  | { ok: true }
  | { ok: false; blocked: React.ReactNode };

export interface ExcelUploadTypeConfig {
  /** Modal title, e.g. "Bulk Norms Upload". */
  title: string;
  /** Noun used in the file-input label: `${entityLabel} File Upload Area`. */
  entityLabel: string;
  /**
   * Where the sample template comes from: a blob URL or client-side generation.
   * Omit it entirely when a type has no blank template to offer — the link is
   * then not rendered (Material-Stores starts from a filled day sheet instead).
   */
  templateSource?:
    | { kind: "blob"; url: string }
    | { kind: "client"; generate: () => void };
  /** Kicks off upload+validation; must resolve to `{status, data: {id}}`. */
  startUpload: (formData: FormData) => Promise<any>;
  /** Builds the JSON `body` part of the multipart request from the comments box. */
  buildBody: (comments: string) => object;
  previewColumns: any[];
  previewScrollX?: number;
  previewPageSize?: number;
  /** Tab buckets for the preview table. Default: All / Valid / Errors. */
  bucketRows?: (rows: any[]) => UploadRowBucket[];
  /** Extra summary chips rendered before the built-in valid/error counts. */
  summaryTags?: (report: UploadReportState) => React.ReactNode;
  /** Label prefix for failed-stage error bullets. Default: `Row ${i + 2}` (page 1 only). */
  rowErrorLabel?: (row: any, index: number) => string;
  copy: {
    /** Intro paragraph shown above the template link on the upload stage. */
    uploadIntro: React.ReactNode;
    validatingHeading: string;
    validatingSub: string;
    committingHeading: string;
    committingSub: string;
    /** Commit button text, also woven into the preview hint ("…then click X to save."). */
    commitCta: string;
    doneHeading: string;
    doneSub?: (report: UploadReportState) => React.ReactNode;
    /** Failed-stage headline when creation (not validation) failed. */
    creationFailedNote?: string;
  };
  /**
   * Optional pre-upload gate, re-run each time the modal opens. While the
   * check is pending a spinner replaces the form; when blocked, the returned
   * node replaces the form (e.g. SequenceGateMessage).
   */
  gate?: { check: () => Promise<UploadGateResult> | UploadGateResult };
  /** Slot rendered between the template link and the file input (e.g. reference CSV links). */
  introSection?: React.ReactNode;
  /** Uploads History route for the poll-timeout hand-off link. */
  historyPath?: string;
  /** When true, cancelling out of the preview discards the pending record (frees the one-active-record slot). */
  discardOnCancelFromPreview?: boolean;
  /** Near-fullscreen modal on every stage (for very wide/long previews). */
  fullScreen?: boolean;
  /**
   * Closing is only possible via the Cancel/X buttons AND goes through a
   * confirmation dialog; mask-click and ESC never close. The terminal 'done'
   * and 'failed' stages close directly (nothing left to protect).
   */
  confirmClose?: boolean;
  /**
   * Enables "Download Report (Excel)" on preview/done/failed: a workbook with
   * All / Errors / Warnings sheets built from the fetched preview rows.
   */
  reportExport?: {
    fileName: string;
    columns: Array<{ header: string; width?: number; value: (row: any) => any }>;
  };
}

const defaultBucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const validRows = rows.filter((row) => !row?.Error);
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Valid (${validRows.length})`, rows: validRows },
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const ExcelUploadStageModal = ({
  config,
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  config: ExcelUploadTypeConfig;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Informational metadata carried on the background pending job, when known. */
  projectId?: number | string | null;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [stage, setStage] = useState<UploadStage>("upload");
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState("");
  const [response, setResponse] = useState({ status: "", message: "" });
  const [previewTab, setPreviewTab] = useState("all");
  const [previewPage, setPreviewPage] = useState(1);
  // reportId captured from the upload response, before the first poll lands
  const [uploadedReportId, setUploadedReportId] = useState<number | null>(null);

  // Latest config without re-running effects on identity changes
  const configRef = useRef(config);
  configRef.current = config;

  const { report, pollTimedOut, progress, startPolling, stopPolling, resetReport } =
    useUploadReportPoll({
      onValidated: () => setStage("preview"),
      onCreated: (record) => {
        setStage("done");
        // Modal owns its own result → drop the watcher's copy so it won't
        // also toast this job (coordination: no double-notify).
        if (record.reportId != null) {
          dispatch(removePendingJob(`import-${record.reportId}`));
        }
        onSuccess?.();
      },
      onFailed: (record, phase) => {
        setStage("failed");
        // Only creation-phase failures ever registered a background job.
        if (phase === "creation" && record.reportId != null) {
          dispatch(removePendingJob(`import-${record.reportId}`));
        }
      },
    });

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, watch, setValue, register, reset, clearErrors } =
    methods;
  const files = watch("attachment");

  const resetAll = useCallback(() => {
    resetReport();
    reset();
    clearErrors();
    setComments("");
    setLoading(false);
    setStage("upload");
    setResponse({ status: "", message: "" });
    setPreviewTab("all");
    setPreviewPage(1);
    setUploadedReportId(null);
  }, [resetReport, reset, clearErrors]);

  useEffect(() => {
    if (open) resetAll();
  }, [open, resetAll]);

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  // ---- gate: re-run each open; spinner while pending; blocked node replaces the form
  const gateGen = useRef(0);
  const [gateState, setGateState] = useState<"ok" | "pending" | "blocked">(
    config.gate ? "pending" : "ok"
  );
  const [gateBlocked, setGateBlocked] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (!open) {
      // park gated modals on the spinner so reopening never flashes the form
      if (configRef.current.gate) setGateState("pending");
      return;
    }
    const gate = configRef.current.gate;
    if (!gate) {
      setGateState("ok");
      setGateBlocked(null);
      return;
    }
    const gen = ++gateGen.current;
    setGateState("pending");
    setGateBlocked(null);
    Promise.resolve(gate.check())
      .then((result) => {
        if (gateGen.current !== gen) return;
        if (result.ok) {
          setGateState("ok");
        } else {
          setGateState("blocked");
          setGateBlocked(result.blocked);
        }
      })
      .catch((error) => {
        // gates are expected to handle their own failures; don't brick the form
        console.error("Upload gate check failed:", error);
        if (gateGen.current !== gen) return;
        setGateState("ok");
      });
  }, [open]);

  const effectiveReportId = report.reportId ?? uploadedReportId;

  const handleUpload = async (values: any) => {
    if (!values.attachment || !values.attachment[0]) {
      setResponse({ status: "error", message: "Please select a file" });
      return;
    }
    // Every parser on the server reads OOXML (a .xlsx is a zip). A legacy .xls
    // or a CSV renamed to .xlsx reaches it as "Can't find end of central
    // directory : is this a zip file?" after a full upload round-trip — a true
    // statement nobody can act on. Catch it here, where the fix is one Save As.
    const picked: File = values.attachment[0];
    if (!/\.xlsx?$/i.test(picked.name) || /\.xls$/i.test(picked.name)) {
      setResponse({
        status: "error",
        message: `"${picked.name}" is not an .xlsx workbook. Open it in Excel and use File → Save As → Excel Workbook (.xlsx); the older .xls format and CSV cannot be read.`,
      });
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("body", JSON.stringify(config.buildBody(comments)));
      formData.append("file", values.attachment[0]);

      const res = await config.startUpload(formData);
      if (res?.status) {
        const reportId = Number(res?.data?.id);
        setUploadedReportId(reportId);
        setStage("validating");
        startPolling(reportId, "validation");
      } else {
        setResponse({
          status: "error",
          message: res?.message || res?.error || "Upload failed",
        });
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setResponse({
        status: "error",
        message:
          error?.response?.data?.message ||
          "Upload failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!effectiveReportId) return;
    try {
      setLoading(true);
      const res = await uploadApis.processExcelCreationById(effectiveReportId);
      if (res?.status) {
        setStage("committing");
        startPolling(effectiveReportId, "creation");
        // Note: while this modal stays open it owns the poll and shows its own
        // result. The job is handed to BackgroundJobWatcher only if the user
        // CLOSES mid-commit (see handleClose) — so the two never poll the same
        // job concurrently (no double-notify).
      } else {
        setResponse({
          status: "error",
          message: res?.message || "Failed to start creation",
        });
      }
    } catch (error) {
      console.error("Creation error:", error);
      setResponse({
        status: "error",
        message: "Something went wrong while starting the creation",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardAndReupload = async () => {
    if (effectiveReportId) {
      try {
        await uploadApis.deleteExcelCreationById(effectiveReportId);
      } catch (error) {
        // superseding on next upload also covers this; ignore
        console.error("delete report failed:", error);
      }
    }
    resetAll();
  };

  const handleClose = () => {
    // Closing mid-commit: hand the still-running creation job to
    // BackgroundJobWatcher so a toast + badge fire when the backend finishes.
    // (Only 'committing' is in-flight; done/failed are already terminal.)
    if (stage === "committing" && effectiveReportId) {
      dispatch(
        addPendingJob({
          jobId: `import-${effectiveReportId}`,
          kind: "import",
          reportId: effectiveReportId,
          label: `${configRef.current.entityLabel} import`,
          historyPath: configRef.current.historyPath ?? "/pms/bulk-upload-list",
          projectId:
            projectId != null && !isNaN(Number(projectId))
              ? Number(projectId)
              : null,
          createdAt: Date.now(),
        })
      );
      dispatch(setUploadHighlight(true));
    }
    stopPolling(); // backend jobs continue unaffected
    onClose();
  };

  // Cancelling out of the preview: optionally discard the pending record so
  // the one-active-record slot frees up immediately (fire-and-forget — the
  // next upload supersedes it anyway).
  const handleCancel = () => {
    if (
      stage === "preview" &&
      configRef.current.discardOnCancelFromPreview &&
      effectiveReportId
    ) {
      uploadApis
        .deleteExcelCreationById(effectiveReportId)
        .catch((error) => console.error("delete report failed:", error));
    }
    handleClose();
  };

  const handleDownloadTemplate = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const source = configRef.current.templateSource;
      if (!source) return;
      if (source.kind === "blob") {
        downloadTemplateFromBlob(source.url);
      } else {
        source.generate();
      }
    },
    []
  );

  // Confirmation-gated close (config.confirmClose): every non-terminal stage
  // asks first; done/failed close directly. Mask/ESC are disabled separately
  // on the Modal props. Plain function (like handleClose/handleCancel) so the
  // closure always sees the current stage/report.
  const requestCancel = () => {
    if (
      !configRef.current.confirmClose ||
      stage === "done" ||
      stage === "failed"
    ) {
      handleCancel();
      return;
    }
    Modal.confirm({
      title: "Cancel this upload?",
      content:
        stage === "committing"
          ? "The save keeps running in the background — you'll be notified when it finishes."
          : stage === "preview"
          ? "The validated preview will be closed. Nothing has been saved yet."
          : "Your progress in this upload will be discarded.",
      okText: "Yes, Cancel",
      okButtonProps: { danger: true },
      cancelText: "Stay",
      onOk: handleCancel,
    });
  };

  // "Download Report (Excel)": All / Errors / Warnings sheets from the
  // (hydrated) preview rows — the offline triage artifact for big files.
  const handleDownloadReport = () => {
    const exp = configRef.current.reportExport;
    if (!exp || !report.rows.length) return;
    const toSheet = (rows: any[]) => {
      const data = rows.map((row) => exp.columns.map((col) => col.value(row)));
      const sheet = XLSX.utils.aoa_to_sheet([
        exp.columns.map((col) => col.header),
        ...data,
      ]);
      sheet["!cols"] = exp.columns.map((col) => ({ wch: col.width ?? 18 }));
      return sheet;
    };
    const errorRows = report.rows.filter((row) => row?.Error);
    const warningRows = report.rows.filter((row) => !row?.Error && row?.Warning);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, toSheet(report.rows), "All Rows");
    XLSX.utils.book_append_sheet(
      workbook,
      toSheet(errorRows),
      `Errors (${errorRows.length})`.slice(0, 31)
    );
    XLSX.utils.book_append_sheet(
      workbook,
      toSheet(warningRows),
      `Warnings (${warningRows.length})`.slice(0, 31)
    );
    XLSX.writeFile(workbook, exp.fileName);
  };

  const reportDownloadButton =
    config.reportExport && report.rows.length ? (
      <Button
        color="primary"
        className="btn-dim btn btn-secondary"
        onClick={handleDownloadReport}
      >
        <Icon name="download" />
        <span>Download Report</span>
      </Button>
    ) : null;

  const buckets = (config.bucketRows ?? defaultBucketRows)(report.rows);
  const activeBucketRows =
    buckets.find((bucket) => bucket.key === previewTab)?.rows || [];
  const previewTabItems = buckets.map(({ key, label }) => ({ key, label }));

  // Failed-stage table gets an inline, fixed-left "Errors" column so each
  // row's problem is readable right against the row (no hover needed) — the
  // long flattened bullet dump above the table has been dropped in favor of it.
  const FAILED_ERROR_COL_WIDTH = 340;
  const failedErrorColumn = {
    title: "Errors",
    key: "__rowErrors",
    fixed: "left" as const,
    width: FAILED_ERROR_COL_WIDTH,
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (!errors.length) return <span className="text-soft">—</span>;
      return (
        <ul
          style={{ margin: 0, paddingLeft: 16, color: "#e85347" }}
        >
          {errors.map((err: string, index: number) => (
            <li key={index} style={{ fontSize: 12, whiteSpace: "normal" }}>
              {err}
            </li>
          ))}
        </ul>
      );
    },
  };
  const failedColumns = [failedErrorColumn, ...config.previewColumns];

  const historyPath = config.historyPath ?? "/pms/bulk-upload-list";
  const uploadsHistoryLink = (
    <span
      className="inlineTableCellLink"
      onClick={() => navigate(historyPath)}
    >
      Uploads History
    </span>
  );

  return (
    <Modal
      title={config.title}
      open={open}
      footer={null}
      onCancel={requestCancel}
      maskClosable={
        config.confirmClose ? false : stage === "upload" && (!files || !files[0])
      }
      keyboard={!config.confirmClose}
      // Validation-display stages (preview / failed) carry wide tables, so give
      // them near-full-viewport width; the other stages stay compact.
      // fullScreen types get near-full viewport on EVERY stage.
      width={
        config.fullScreen
          ? "calc(100vw - 40px)"
          : stage === "preview" || stage === "failed"
          ? "96%"
          : "70%"
      }
      style={
        config.fullScreen
          ? { maxWidth: "calc(100vw - 40px)", top: 12 }
          : { maxWidth: stage === "preview" || stage === "failed" ? 1600 : 800 }
      }
      // fullScreen: pin the modal shell to the viewport height so every stage
      // fills the screen; the body flexes and scrolls vertically inside it.
      // overflowX hidden: the preview table brings its own x-scroll container,
      // so any body-level horizontal scrollbar is just layout noise.
      styles={
        config.fullScreen
          ? {
              content: {
                height: "calc(100vh - 44px)",
                display: "flex",
                flexDirection: "column",
              },
              body: { flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 },
            }
          : undefined
      }
    >
      {/* ---------------- upload ---------------- */}
      {stage === "upload" &&
        (gateState === "pending" ? (
          <Alert color="info" className="mt-3 d-flex align-items-center">
            <Spinner size="sm" className="me-2" />
            Checking configuration...
          </Alert>
        ) : gateState === "blocked" ? (
          gateBlocked
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleUpload)}>
              {config.copy.uploadIntro}
              {config.templateSource && (
                <p className="mb-0">
                  <a
                    href="#"
                    onClick={handleDownloadTemplate}
                    style={{ textDecoration: "underline" }}
                  >
                    <Icon name="download" /> Download Sample Template
                  </a>
                </p>
              )}
              {config.introSection}
              <Row className="mt-3">
                <Col md={12}>
                  {files && files[0]?.name ? (
                    <div className="form-group m-0">
                      <div className="form-label-wrapper">
                        <label className="form-label" htmlFor={"attachment"}>
                          {config.entityLabel} File Upload Area
                        </label>
                      </div>
                      <div
                        className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                        style={{ borderRadius: "3px" }}
                      >
                        <span>{files[0]?.name}</span>
                        <span
                          onClick={() => {
                            setValue("attachment", null);
                            clearErrors("attachment");
                          }}
                          style={{
                            cursor: "pointer",
                            marginLeft: "10px",
                            display: "block",
                            fontSize: 14,
                          }}
                        >
                          <Icon name="cross"></Icon> &nbsp;
                        </span>
                      </div>
                    </div>
                  ) : (
                    <FormInput
                      type="file"
                      label={`${config.entityLabel} File Upload Area`}
                      accept=".xlsx"
                      required={false}
                      {...register("attachment")}
                    />
                  )}
                </Col>
              </Row>

              <Row className="mt-2">
                <Col md={12}>
                  <div className="row g-3 mt-1">
                    <div className="col-12">
                      <label className="form-label">Comments</label>
                      <Input.TextArea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Enter comments"
                        rows={3}
                        style={{ resize: "vertical" }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <Alert
                isOpen={response?.status === "error"}
                color="danger"
                className="start-0 w-100 py-1 mt-2"
                style={{ top: 0, textAlign: "left", fontSize: "14px" }}
              >
                <ReactTyped
                  strings={[response?.message]}
                  typeSpeed={30}
                  showCursor={false}
                />
              </Alert>

              <Row className="mt-4">
                <Col>
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button color="primary" type="submit">
                          Upload &amp; Validate
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="primary"
                          onClick={handleClose}
                          className="btn-dim btn btn-secondary"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                </Col>
              </Row>
            </form>
          </FormProvider>
        ))}

      {/* ---------------- validating / committing ---------------- */}
      {(stage === "validating" || stage === "committing") && (
        <div
          className="text-center py-5"
          // fullScreen: center the spinner block vertically in the tall body
          style={
            config.fullScreen
              ? {
                  height: "100%",
                  minHeight: "calc(100vh - 220px)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }
              : undefined
          }
        >
          <Spinner color="primary" />
          <h6 className="mt-3">
            {stage === "validating"
              ? config.copy.validatingHeading
              : config.copy.committingHeading}
          </h6>
          <p className="text-soft mb-1">
            {stage === "validating"
              ? config.copy.validatingSub
              : config.copy.committingSub}
          </p>
          {progress && (
            <p
              className="mb-1"
              style={{ color: "#2557d6", fontWeight: 600, fontSize: 14 }}
            >
              {progress}
            </p>
          )}
          {pollTimedOut && (
            <Alert color="warning" className="mt-3 py-2">
              This is taking longer than expected. The job keeps running in the
              background — you can safely close this window and check{" "}
              {uploadsHistoryLink} later.
              {stage === "committing" && (
                <> We'll also notify you here when it's done.</>
              )}
            </Alert>
          )}
        </div>
      )}

      {/* ---------------- preview ---------------- */}
      {stage === "preview" && (
        <div>
          <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
            {config.summaryTags?.(report)}
            <Tag color="green">{report.successCount} valid line(s)</Tag>
            {report.failureCount > 0 && (
              <Tag color="red">{report.failureCount} error(s)</Tag>
            )}
          </div>
          <p className="text-soft mb-2" style={{ fontSize: 13 }}>
            Nothing has been saved yet — review the rows below
            {report.failureCount > 0
              ? ". Rows with errors block creation: fix the file and re-upload."
              : `, then click ${config.copy.commitCta} to save.`}
          </p>
          <Tabs
            activeKey={previewTab}
            onChange={(key) => {
              setPreviewTab(key);
              setPreviewPage(1); // reset paging when switching bucket
            }}
            items={previewTabItems}
            size="small"
            className="mb-0"
          />
          {(() => {
            const previewPageSize = config.previewPageSize ?? 50;
            const pagedRows = activeBucketRows
              .slice(
                (previewPage - 1) * previewPageSize,
                previewPage * previewPageSize
              )
              .map((row, index) => ({ ...row, key: index }));
            return (
              <>
                <Table
                  columns={config.previewColumns}
                  dataSource={pagedRows}
                  size="small"
                  scroll={{
                    x: config.previewScrollX ?? 1100,
                    y: config.fullScreen ? ("calc(100vh - 390px)" as any) : 340,
                  }}
                  pagination={false}
                />
                {report.totalRecords > report.rows.length && (
                  <p className="text-soft mb-0 mt-1" style={{ fontSize: 12 }}>
                    Loaded {report.rows.length} of {report.totalRecords} rows —
                    fetching the rest… (the full file is already validated
                    server-side)
                  </p>
                )}
                <Alert
                  isOpen={response?.status === "error"}
                  color="danger"
                  className="start-0 w-100 py-1 mt-2"
                  style={{ textAlign: "left", fontSize: "14px" }}
                >
                  {response?.message}
                </Alert>
                {/* Footer: action buttons on the left, pagination on the right,
                    aligned on a single row. */}
                <div
                  className="d-flex align-items-center justify-content-between mt-3"
                  style={{ gap: 12, flexWrap: "wrap" }}
                >
                  <div className="d-flex" style={{ gap: 12 }}>
                    {report.failureCount === 0 ? (
                      loading ? (
                        <Button color="primary">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button color="primary" onClick={handleCreate}>
                          <Icon name="check" />
                          <span>{config.copy.commitCta}</span>
                        </Button>
                      )
                    ) : (
                      <Button
                        color="danger"
                        onClick={handleDiscardAndReupload}
                      >
                        <Icon name="upload" />
                        <span>Fix File &amp; Re-upload</span>
                      </Button>
                    )}
                    {reportDownloadButton}
                    <Button
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      onClick={requestCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                  {activeBucketRows.length > previewPageSize && (
                    <Pagination
                      current={previewPage}
                      total={activeBucketRows.length}
                      pageSize={previewPageSize}
                      showSizeChanger={false}
                      onChange={setPreviewPage}
                      size="small"
                    />
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ---------------- done ---------------- */}
      {stage === "done" && (
        <div className="text-center py-4">
          <Icon
            name="check-circle"
            style={{ fontSize: 42, color: "#1ee0ac" }}
          />
          <h6 className="mt-2">{config.copy.doneHeading}</h6>
          {config.copy.doneSub && (
            <p className="text-soft">{config.copy.doneSub(report)}</p>
          )}
          <div className="d-flex justify-content-center" style={{ gap: 12 }}>
            {reportDownloadButton}
            <Button color="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- failed ---------------- */}
      {stage === "failed" &&
        (() => {
          const errorRows = report.rows
            .filter((row) => row?.Error)
            .map((row, index) => ({ ...row, key: index }));
          return (
            <div>
              <Alert color="danger" className="py-2">
                <strong>
                  {report.uploadStatus === "Submit"
                    ? config.copy.creationFailedNote ||
                      "Creation failed — nothing was saved."
                    : "Validation failed — nothing was saved."}
                </strong>
                {report.topError ? (
                  <div className="mt-1">{report.topError}</div>
                ) : null}
                {errorRows.length > 0 ? (
                  <div className="mt-1">
                    {report.failureCount || errorRows.length} row(s) have
                    errors — each row's issue is shown in the{" "}
                    <strong>Errors</strong> column below.
                  </div>
                ) : null}
              </Alert>
              {errorRows.length > 0 && (
                <Table
                  columns={failedColumns}
                  dataSource={errorRows}
                  size="small"
                  scroll={{
                    x: (config.previewScrollX ?? 1100) + FAILED_ERROR_COL_WIDTH,
                    y: 320,
                  }}
                  pagination={{
                    pageSize: config.previewPageSize ?? 50,
                    hideOnSinglePage: true,
                  }}
                />
              )}
              <div className="d-flex mt-3" style={{ gap: 12 }}>
                <Button color="primary" onClick={handleDiscardAndReupload}>
                  <Icon name="upload" />
                  <span>Fix File &amp; Re-upload</span>
                </Button>
                {reportDownloadButton}
                <Button
                  color="primary"
                  className="btn-dim btn btn-secondary"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </div>
            </div>
          );
        })()}
    </Modal>
  );
};

export default ExcelUploadStageModal;
